// src/pages/yachts/special-offers/ManageSpecialOfferPresenter.js
import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Field, FieldArray, reduxForm, formValueSelector, change } from 'redux-form';
import { Row, Col, Button, FormGroup, Label } from 'reactstrap';

import YachtMenu from '../components/YachtMenu';
import Loader from '../../../common/components/Loader';
import RenderField from '../../../common/components/RenderField';
import LocationTree from '../../../common/components/locations/LocationTree';
import validate, {
    OFFER_TYPES,
    AUDIENCES,
    CURRENCIES,
    OFFER_TYPE_LABELS,
    AUDIENCE_LABELS
} from './validate';
import RatesTimeline from './components/RatesTimeline';
import TiersEditor from './components/TiersEditor';
import BrokersPicker from './components/BrokersPicker';
import SimulateDrawer from './SimulateDrawer';
import { humanize } from './parseApiError';
import advertisedLocationIds from './advertisedLocations';

const sectionHeaderStyle = {
    fontSize: 13,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: '#41506e',
    marginTop: 32,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottom: '1px solid #e6e9f1'
};

const SectionHeader = ({ children, optional }) => (
    <div style={sectionHeaderStyle}>
        {children}
        {optional && (
            <span style={{ marginLeft: 6, fontWeight: 400, textTransform: 'none', color: '#8a96b1' }}>
                (optional)
            </span>
        )}
    </div>
);

const renderSelect = ({ input, label, options, labels, meta }) => (
    <FormGroup className="so-select-group">
        {label && <Label>{label}</Label>}
        <select className="form-control" {...input}>
            <option value="">-- Select --</option>
            {options.map((o) => (
                <option key={o} value={o}>
                    {labels && labels[o] ? labels[o] : o}
                </option>
            ))}
        </select>
        {meta.touched && meta.error && (
            <div className="text-danger">{meta.error}</div>
        )}
    </FormGroup>
);

// Rate currencies are stored lower-case; display them upper-case.
const CURRENCY_LABELS = CURRENCIES.reduce(
    (acc, c) => ({ ...acc, [c]: c.toUpperCase() }),
    {}
);

const renderTextarea = ({ input, label, meta }) => (
    <FormGroup>
        {label && <Label>{label}</Label>}
        <textarea
            className="form-control"
            rows={3}
            style={{ minHeight: 80, resize: 'vertical' }}
            {...input}
        />
        {meta.touched && meta.error && (
            <div className="text-danger">{meta.error}</div>
        )}
    </FormGroup>
);

let ManageSpecialOfferPresenter = (props) => {
    return (
        <div className="content-inner-wrapper special-offers-page">
            <YachtMenu
                user_role={props.user_role}
                match={props.match}
                history={props.history}
            />
            <div className="content-inner">
                {props.loading ? (
                    <Loader style={{ marginTop: '120px' }} />
                ) : (
                    <form onSubmit={props.handleSubmit(props.onSubmit)}>
                        <Row>
                            <Col xs="12" lg="9">
                                <div className="d-flex align-items-center mb-3">
                                    <h2 className="mb-0">
                                        {props.isEdit
                                            ? 'Edit special offer'
                                            : 'New special offer'}
                                    </h2>
                                    {props.isEdit && (
                                        <Button
                                            type="button"
                                            color="secondary"
                                            className="ml-auto mr-2"
                                            onClick={props.onOpenSimulate}
                                        >
                                            Simulate
                                        </Button>
                                    )}
                                    <Button
                                        type="submit"
                                        color="primary"
                                        className={props.isEdit ? '' : 'ml-auto'}
                                        disabled={props.submitting}
                                    >
                                        Save
                                    </Button>
                                </div>

                                {props.submitError && (
                                    <div className="alert alert-danger">
                                        {props.submitError}
                                        {props.conflictOfferId && (
                                            <>
                                                {' '}
                                                <Link
                                                    to={`/yachts/edit/charter/${props.match.params.id}/special-offers/${props.conflictOfferId}/edit`}
                                                >
                                                    View conflicting offer
                                                </Link>
                                            </>
                                        )}
                                    </div>
                                )}

                                {props.warnings && props.warnings.length > 0 && (
                                    <div className="alert alert-warning">
                                        <strong>Saved, but check the following:</strong>
                                        <ul className="mb-2 mt-2 pl-4">
                                            {props.warnings.map((w) => (
                                                <li key={w}>{humanize(w)}</li>
                                            ))}
                                        </ul>
                                        <Link to={props.warningsHref}>Back to special offers</Link>
                                    </div>
                                )}

                                <RatesTimeline
                                    rates={props.rates || []}
                                    dateFrom={props.dateFrom}
                                    dateTo={props.dateTo}
                                    onMatch={props.onMatchedRate}
                                />

                                <SectionHeader>Identification</SectionHeader>
                                <Field
                                    name="name"
                                    type="text"
                                    placeholder="Name"
                                    group="name"
                                    component={RenderField}
                                />
                                <Field
                                    name="description"
                                    label="Description"
                                    component={renderTextarea}
                                />

                                <SectionHeader>Type</SectionHeader>
                                <Field
                                    name="offer_type"
                                    label="Offer type"
                                    options={OFFER_TYPES}
                                    labels={OFFER_TYPE_LABELS}
                                    component={renderSelect}
                                />
                                {props.offerType === 'percentage' && (
                                    <Field
                                        name="discount_percent"
                                        type="number"
                                        placeholder="Discount % (0–100)"
                                        group="discount_percent"
                                        component={RenderField}
                                    />
                                )}
                                {props.offerType === 'flat_amount' && (
                                    <Row>
                                        <Col xs="8">
                                            <Field
                                                name="discount_amount"
                                                type="number"
                                                placeholder="Flat amount"
                                                group="discount_amount"
                                                component={RenderField}
                                            />
                                        </Col>
                                        <Col xs="4">
                                            <Field
                                                name="discount_amount_currency"
                                                options={CURRENCIES}
                                                labels={CURRENCY_LABELS}
                                                component={renderSelect}
                                            />
                                        </Col>
                                    </Row>
                                )}
                                {props.offerType === 'free_days' && (
                                    <Field
                                        name="free_days"
                                        type="number"
                                        placeholder="Free days"
                                        group="free_days"
                                        component={RenderField}
                                    />
                                )}
                                {(props.offerType === 'tiered_discount' ||
                                    props.offerType === 'package') && (
                                    <FieldArray
                                        name="tiers"
                                        component={TiersEditor}
                                        offerType={props.offerType}
                                    />
                                )}

                                <SectionHeader>Charter window</SectionHeader>
                                <Row>
                                    <Col xs="6">
                                        <Field
                                            name="date_from"
                                            type="date"
                                            placeholder="Date from"
                                            group="date_from"
                                            component={RenderField}
                                        />
                                    </Col>
                                    <Col xs="6">
                                        <Field
                                            name="date_to"
                                            type="date"
                                            placeholder="Date to"
                                            group="date_to"
                                            component={RenderField}
                                        />
                                    </Col>
                                </Row>
                                <FormGroup check className="mb-2">
                                    <Label check>
                                        <Field
                                            name="allow_partial_application"
                                            component="input"
                                            type="checkbox"
                                        />{' '}
                                        Apply to the booked days inside this window only
                                    </Label>
                                    <div
                                        className="text-muted"
                                        style={{ fontSize: 12, marginTop: 2 }}
                                    >
                                        Unchecked, a charter running outside this window pays
                                        full price and the offer does not apply at all.
                                        Checked, the offer applies to the booked days that
                                        fall inside the window.
                                    </div>
                                    {props.offerType === 'package' && (
                                        <div
                                            style={{ fontSize: 12, marginTop: 2, color: '#8a6d3b' }}
                                        >
                                            Package offers save this setting but do not use it
                                            yet — pricing still requires the whole charter to
                                            fall inside the window.
                                        </div>
                                    )}
                                </FormGroup>

                                <SectionHeader optional>Locations</SectionHeader>
                                {props.allowedLocationIds && (
                                    <div
                                        className="text-muted"
                                        style={{ fontSize: 12, marginBottom: 6 }}
                                    >
                                        {props.allowedLocationIds.length
                                            ? 'Only destinations this yacht has rates for between ' +
                                              props.dateFrom +
                                              ' and ' +
                                              props.dateTo +
                                              ' can be selected.'
                                            : 'No rated season of this yacht covers ' +
                                              props.dateFrom +
                                              ' to ' +
                                              props.dateTo +
                                              ' — no destination can be selected.'}
                                    </div>
                                )}
                                <Field
                                    name="location_ids"
                                    component={LocationTree}
                                    label="Restrict to specific destinations (leave empty to apply everywhere)"
                                    mode="free"
                                    should_cascade={true}
                                    check_model="all"
                                    only_for_yachts={true}
                                    allowed_ids={props.allowedLocationIds}
                                />

                                <SectionHeader>Publishing window</SectionHeader>
                                <Row>
                                    <Col xs="6">
                                        <Field
                                            name="publish_start_date"
                                            type="datetime-local"
                                            placeholder="Publish from"
                                            group="publish_start_date"
                                            component={RenderField}
                                        />
                                    </Col>
                                    <Col xs="6">
                                        <Field
                                            name="publish_end_date"
                                            type="datetime-local"
                                            placeholder="Publish to"
                                            group="publish_end_date"
                                            component={RenderField}
                                        />
                                    </Col>
                                </Row>

                                <SectionHeader optional>Booking window</SectionHeader>
                                <Row>
                                    <Col xs="6">
                                        <Field
                                            name="booking_window_start"
                                            type="date"
                                            placeholder="Booking window start"
                                            group="booking_window_start"
                                            component={RenderField}
                                        />
                                    </Col>
                                    <Col xs="6">
                                        <Field
                                            name="booking_window_end"
                                            type="date"
                                            placeholder="Booking window end"
                                            group="booking_window_end"
                                            component={RenderField}
                                        />
                                    </Col>
                                </Row>

                                <SectionHeader optional>Duration limits</SectionHeader>
                                <Row>
                                    <Col xs="6">
                                        <Field
                                            name="min_charter_days"
                                            type="number"
                                            placeholder="Min charter days"
                                            group="min_charter_days"
                                            component={RenderField}
                                        />
                                    </Col>
                                    <Col xs="6">
                                        <Field
                                            name="max_charter_days"
                                            type="number"
                                            placeholder="Max charter days"
                                            group="max_charter_days"
                                            component={RenderField}
                                        />
                                    </Col>
                                </Row>

                                <SectionHeader>Visibility</SectionHeader>
                                <Field
                                    name="audience"
                                    label="Audience"
                                    options={AUDIENCES}
                                    labels={AUDIENCE_LABELS}
                                    component={renderSelect}
                                />
                                <Field
                                    name="access_code"
                                    type="text"
                                    placeholder="Access code (optional — hides this offer)"
                                    group="access_code"
                                    component={RenderField}
                                />
                                {props.audience === 'broker_restricted' && (
                                    <Field
                                        name="allowed_broker_ids"
                                        component={BrokersPicker}
                                    />
                                )}

                                <SectionHeader>State</SectionHeader>
                                <FormGroup check className="mb-4">
                                    <Label check>
                                        <Field
                                            name="is_active"
                                            component="input"
                                            type="checkbox"
                                        />{' '}
                                        Active (offer is bookable when checked)
                                    </Label>
                                </FormGroup>
                                <Field
                                    name="priority"
                                    type="number"
                                    placeholder="Priority (advanced; default 0)"
                                    group="priority"
                                    component={RenderField}
                                />

                                <div className="d-flex mt-4 mb-5">
                                    <Button
                                        type="submit"
                                        color="primary"
                                        size="lg"
                                        className="ml-auto"
                                        disabled={props.submitting}
                                    >
                                        Save offer
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </form>
                )}
            </div>
            <SimulateDrawer
                open={!!props.simulateOpen}
                onClose={props.onCloseSimulate}
                yachtId={Number(props.match.params.id)}
                initialFrom={props.dateFrom}
                initialTo={props.dateTo}
            />
        </div>
    );
};

ManageSpecialOfferPresenter = reduxForm({
    form: 'special_offer_form',
    enableReinitialize: true,
    validate
})(ManageSpecialOfferPresenter);

const selector = formValueSelector('special_offer_form');

const mapStateToProps = (state, ownProps) => ({
    initialValues: ownProps.initialValues,
    offerType: selector(state, 'offer_type'),
    audience: selector(state, 'audience'),
    dateFrom: selector(state, 'date_from'),
    dateTo: selector(state, 'date_to'),
    currentCurrency: selector(state, 'discount_amount_currency'),
    rates: state.yacht_special_offers.rates,
    allowedLocationIds: advertisedLocationIds(
        state.yacht_special_offers.seasons,
        selector(state, 'date_from'),
        selector(state, 'date_to')
    )
});

const mapDispatchToPropsV2 = (dispatch) => ({
    _setCurrency: (currency) =>
        dispatch(change('special_offer_form', 'discount_amount_currency', currency))
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    onMatchedRate: (matched) => {
        if (!matched || !matched.currency) return;
        if (stateProps.offerType !== 'flat_amount') return;
        if (stateProps.currentCurrency) return; // don't clobber user input
        dispatchProps._setCurrency(String(matched.currency).toLowerCase());
    }
});

export default connect(mapStateToProps, mapDispatchToPropsV2, mergeProps)(ManageSpecialOfferPresenter);
